import { openModal } from "action/modal/openModal";
import getProductLoyaltyRewardsApi from "api/product/getProductLoyaltyRewardsApi";
import PAImage from "component/design-system/images/PAImage";
import Popover from "component/design-system/popover/Popover";
import PAText, { FONT_SIZE } from "component/design-system/typography/PAText/PAText";
import useScreenSize from "hoc/useScreenSize";
import { RootState } from "interfaces/generics.type";
import { useTranslation } from "next-i18next";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getRelativeImagePath } from "util/helper/getRelativeImagePath";
import isFeatureEnabled from "util/helper/isFeatureEnabled";
import HideIf from "util/HideIf";
import styles from "./productRewardsBadge.module.scss";

interface IProps {
  sticky?: boolean;
  sku?: string;
}

const rewardsCache: Record<string, number> = {};

function ProductRewardsBadge({ sticky, sku }: IProps) {
  const { t } = useTranslation("product");
  const [isMobile] = useScreenSize();
  const dispatch = useDispatch();
  const { loggedIn, expConfigurations } = useSelector((state: RootState) => state.authenticationState);
  const { currentPageType } = useSelector((store: RootState) => store.utils);
  const [productRewards, setProductRewards] = useState<number>(0);

  const isLoyaltyEnabled = isFeatureEnabled(expConfigurations, "loyalty");
  const showRewardsBadge = currentPageType === "PRODUCT_PAGE" && Boolean(sku) && isLoyaltyEnabled;

  async function getCachedProductLoyaltyRewards(productSku: string): Promise<number> {
    if (rewardsCache[productSku] !== undefined) {
      return rewardsCache[productSku];
    }

    const res = await getProductLoyaltyRewardsApi([productSku]);
    const rewardPoints = res.data.skuToLoyaltyPointsMap[productSku] || 0;
    rewardsCache[productSku] = rewardPoints;
    return rewardPoints;
  }

  useEffect(() => {
    if (!showRewardsBadge) return;

    getCachedProductLoyaltyRewards(sku).then((points) => {
      setProductRewards(points);
    });
  }, [sku, showRewardsBadge]);

  if (!showRewardsBadge || productRewards === 0) {
    return null;
  }

  return (
    <div className={styles.product_rewards_badge_container}>
      <div className={styles.product_rewards_badge}>
        <Popover
          placement={(sticky || isMobile) ? "bottom" : "right"}
          component={(
            <div className={styles.earn_rewards_info}>
              <HideIf show={loggedIn}>
                <PAText fontSize={FONT_SIZE[0]} color="black" className="m-r-5">
                  {t("Earn")}
                </PAText>
                <PAText fontSize={FONT_SIZE[0]} color="black" bold className="m-r-5">
                  {`${productRewards} ${t("Reward")}`}
                </PAText>
                <PAText fontSize={FONT_SIZE[0]} color="black">
                  {t("Points when you buy this product.")}
                </PAText>
              </HideIf>
              <HideIf show={!loggedIn}>
                <PAText
                  fontSize={FONT_SIZE[0]}
                  color="black"
                  bold
                  className={styles.open_login}
                  onClick={() => dispatch(openModal("login", {}))}
                >
                  {t("Login")}
                </PAText>
                <PAText fontSize={FONT_SIZE[0]} color="black" className="m-r-5">
                  {t("or")}
                </PAText>
                <PAText
                  fontSize={FONT_SIZE[0]}
                  color="pa-blue"
                  className={styles.open_login}
                  underline
                  bold
                  onClick={() => dispatch(openModal("signup", {}))}
                >
                  {t("Join Avatar Club")}
                </PAText>
                <PAText fontSize={FONT_SIZE[0]} color="black">
                  {t("to earn points and exclusive perks.")}
                </PAText>
              </HideIf>
              <PAText fontSize={FONT_SIZE[0]} color="black" component="p" className="m-t-10">
                {t("Redeem points on future orders.")}
              </PAText>
            </div>
          )}
        >
          <div className={styles.reward_point_text}>
            <PAImage
              src={getRelativeImagePath("/cms/pubicImages/images/rewards-pa.png")}
              alt="Rewards Icon"
              width={32}
              height={32}
              className={styles.rewards_pa_image}
            />
            <div>
              <PAText fontSize={FONT_SIZE[0]} bold className={`m-r-5 ${styles.uppercase}`}>
                {`${t("Earn")} ${productRewards} ${t("points")}`}
              </PAText>
              <PAText fontSize={FONT_SIZE[0]} className={styles.hide_desktop}>
                {t("with this purchase")}
              </PAText>
            </div>
            <div className={styles.info_icon_container}>
              <i className={`fa fa-info ${styles.info_icon}`} />
            </div>
          </div>

        </Popover>
      </div>
    </div>
  );
}

export default ProductRewardsBadge;
